import React, { useRef, useLayoutEffect, useState, useMemo } from "react";
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';


const NodeBox = React.forwardRef(function NodeBox({ text, status, shouldBlink }, ref) {
  const statusClass = status === "critical" ? "status-critical"
    : status === "warning" ? "status-warning"
      : status === "healthy" ? "status-healthy"
        : "status-default";

  return (
    <div ref={ref} className={`node-box ${statusClass}`}>
      <div className="node-text">{text}</div>
      {shouldBlink && <div className="node-circular-note">(Circular dependency)</div>}
    </div>
  );
});


const UpwardTreeView = ({ contractName, contractStatus, dependencies }) => {
  // outer scroll wrapper (if you keep overflow on outer)
  const scrollRef = useRef(null);
  // inner container that holds the scale wrapper (padding etc.)
  const containerRef = useRef(null);
  // wrapper we apply translate+scale to
  const scaleWrapperRef = useRef(null);

  const nodeRefs = useRef({});       // instanceKey -> DOM node
  const connectionsRef = useRef([]); // list of { fromKey, toKey }
  const [paths, setPaths] = useState([]);
  const [svgSize, setSvgSize] = useState({ width: 0, height: 0 });

  // pan & zoom
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const panStartRef = useRef({ x: 0, y: 0 });

  // reset connections each render
  connectionsRef.current = [];

  const root = useMemo(() => ({
    id: "_root_",
    name: contractName,
    status: contractStatus,
    dependencies: Array.isArray(dependencies) ? dependencies : []
  }), [contractName, contractStatus, dependencies]);

  const registerConnection = (fromKey, toKey) => {
    connectionsRef.current.push({ fromKey, toKey });
  };

  /* RenderNode (upward layout: parents above, children below is not used here) */
  function RenderNode({ node, path = "0", visited = new Set() }) {
    if (!node) return null;

    const nodeId = node.id ?? node.name ?? `node-${path}`;
    const instanceKey = `${nodeId}#${path}`;
    const isRootNode = nodeId === "_root_";
    const shouldBlink = node.name?.trim() === contractName.trim() && !isRootNode;
    // If seen on current path, render node with ref but don't recurse
    if (visited.has(nodeId)) {
      const assignRef = (el) => { if (el) nodeRefs.current[instanceKey] = el; };
      return (
        <div className="node-column">
          <div className={`node-instance ${shouldBlink ? "node-blink" : ""}`}>
            <NodeBox ref={assignRef} text={node.name ?? nodeId} status={node.status} shouldBlink={shouldBlink} />
          </div>
        </div>
      );
    }

    const parents = Array.isArray(node.dependencies) ? node.dependencies : [];

    // register parent -> this instance for every parent
    parents.forEach((p, idx) => {
      const fromKey = `${(p.id ?? p.name ?? `p-${path}-${idx}`)}#${path}-${idx}`;
      registerConnection(fromKey, instanceKey);
    });

    return (
      <div className="node-column">
        {parents.length > 0 && (
          <div className="parents-row">
            {parents.map((p, idx) => (
              <div key={`${(p.id ?? p.name ?? `p-${path}-${idx}`)}#${path}-${idx}`} className="parent-column">
                <RenderNode node={p} path={`${path}-${idx}`} visited={new Set([...visited, nodeId])} />
              </div>
            ))}
          </div>
        )}

        {parents.length > 0 && <div className="spacer" />}

        <div className={`node-instance ${shouldBlink ? "node-blink" : ""}`}>
          <NodeBox
            ref={(el) => { if (el) nodeRefs.current[instanceKey] = el; }}
            text={node.name ?? nodeId}
            status={node.status}
            shouldBlink={shouldBlink}
          />
        </div>
      </div>
    );
  }

  // pan/zoom handlers
  const startDrag = (clientX, clientY) => {
    setIsDragging(true);
    dragStartRef.current = { x: clientX, y: clientY };
    panStartRef.current = { ...pan };
    document.body.style.userSelect = "none";
  };
  const onPointerDown = (e) => {
    const cx = e.clientX ?? (e.touches && e.touches[0]?.clientX);
    const cy = e.clientY ?? (e.touches && e.touches[0]?.clientY);
    if (cx == null) return;
    startDrag(cx, cy);
  };
  const onPointerMove = (e) => {
    if (!isDragging) return;
    const cx = e.clientX ?? (e.touches && e.touches[0]?.clientX);
    const cy = e.clientY ?? (e.touches && e.touches[0]?.clientY);
    if (cx == null) return;
    const dx = cx - dragStartRef.current.x;
    const dy = cy - dragStartRef.current.y;
    setPan({ x: panStartRef.current.x + dx, y: panStartRef.current.y + dy });
  };
  const onPointerUp = () => {
    setIsDragging(false);
    document.body.style.userSelect = "";
  };

  const handleZoomIn = () => setZoom(z => Math.min(2.5, +(z * 1.2).toFixed(3)));
  const handleZoomOut = () => setZoom(z => Math.max(0.4, +(z / 1.2).toFixed(3)));
  const handleReset = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  /**
   * Important: compute coordinates in wrapper (scaleWrapperRef) user coordinates.
   * Because we apply translate+scale to the wrapper, bounding rects reflect transforms.
   * Steps:
   *  - wrapperRect = wrapper.getBoundingClientRect()
   *  - elemRect = elem.getBoundingClientRect()
   *  - screen coords relative to wrapper: sx = elemRect.left - wrapperRect.left (+ width/2) etc.
   *  - convert to SVG user coords by dividing by zoom: x = sx / zoom
   *  - set svg width/height = wrapperRect.width/zoom, wrapperRect.height/zoom
   */
  useLayoutEffect(() => {
    const scrollEl = scrollRef.current;
    const wrapper = scaleWrapperRef.current;
    if (!scrollEl || !wrapper) return;

    // ensure padding exists (fallback)
    const containerEl = containerRef.current;
    if (containerEl && !containerEl.style.padding) containerEl.style.padding = "40px";

    let mounted = true;

    const computeAll = () => {
      if (!mounted) return;
      const wrapperRect = wrapper.getBoundingClientRect();

      const newPaths = [];
      for (const conn of connectionsRef.current) {
        const fromEl = nodeRefs.current[conn.fromKey];
        const toEl = nodeRefs.current[conn.toKey];
        if (!fromEl || !toEl) continue;

        const fRect = fromEl.getBoundingClientRect();
        const tRect = toEl.getBoundingClientRect();

        // screen coords relative to wrapper
        const sx1 = fRect.left + fRect.width / 2 - wrapperRect.left;
        const sy1 = fRect.top + fRect.height - wrapperRect.top; // parent bottom (upward layout)
        const sx2 = tRect.left + tRect.width / 2 - wrapperRect.left;
        const sy2 = tRect.top - wrapperRect.top; // child top

        // convert to user coords (divide by zoom)
        const x1 = sx1 / zoom;
        const y1 = sy1 / zoom;
        const x2 = sx2 / zoom;
        const y2 = sy2 / zoom;

        const midY = (y1 + y2) / 2;
        newPaths.push(`M ${x1} ${y1} C ${x1} ${midY} ${x2} ${midY} ${x2} ${y2}`);
      }

      // size svg to wrapper size in user coords
      const svgW = wrapperRect.width / zoom;
      const svgH = wrapperRect.height / zoom;

      if (!mounted) return;
      setPaths(newPaths);
      setSvgSize({ width: svgW, height: svgH });
    };

    computeAll();
    const raf = requestAnimationFrame(computeAll);

    const onScroll = () => requestAnimationFrame(computeAll);
    const onResize = () => requestAnimationFrame(computeAll);

    scrollEl.addEventListener("scroll", onScroll);
    window.addEventListener("resize", onResize);

    return () => {
      mounted = false;
      cancelAnimationFrame(raf);
      scrollEl.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [root, zoom, pan]); // recompute when zoom/pan change

  // apply transform to wrapper (pan + zoom)
  const transformStyle = { transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: "0 0" };

  return (
    <div className="relative overflow-hidden">
      {/* Controls */}
      <div className="absolute top-3 right-3 z-20 flex gap-2">
        <button onClick={handleZoomIn} className="p-2 bg-white border rounded shadow-sm"><ZoomIn className="  h-4 text-gray-600" /></button>
        <button onClick={handleZoomOut} className="p-2 bg-white border rounded shadow-sm"><ZoomOut className="  h-4 text-gray-600" /></button>
        <button onClick={handleReset} className="p-2 bg-white border rounded shadow-sm"> <RotateCcw className="  h-4 text-gray-600" /></button>
      </div>

      {/* outer scroll wrapper */}
      <div
        ref={scrollRef}
        className="bg-white rounded-sm border border-gray-200 cursor-grab active:cursor-grabbing"
        onMouseDown={onPointerDown}
        onMouseMove={onPointerMove}
        onMouseUp={onPointerUp}
        onMouseLeave={onPointerUp}
        onTouchStart={(e) => onPointerDown(e.touches ? e.touches[0] : e)}
        onTouchMove={(e) => onPointerMove(e.touches ? e.touches[0] : e)}
        onTouchEnd={onPointerUp}
      >
        <div className="upward-tree-root">
          <div className="upward-tree-container" ref={containerRef}>
            {/* wrapper that receives pan+zoom */}
            <div ref={scaleWrapperRef} className="upward-scale-wrapper" style={transformStyle}>
              <div className="upward-tree-center">
                <RenderNode node={root} path="root" visited={new Set()} />
              </div>

              {/* SVG overlay sized to wrapper user coords */}
              <svg
                className="upward-svg w-full h-full"
                xmlns="http://www.w3.org/2000/svg"
                width={svgSize.width}
                height={svgSize.height}
                style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none", overflow: "visible" }}
              >
                <g className="connector-group">
                  {paths.map((p, i) => (
                    <path key={i} d={p} className="connector-path" />
                  ))}
                </g>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpwardTreeView;
